import {html, LitElement} from 'lit';
import type {PropertyValues} from 'lit';
import {localized, msg} from '@lit/localize';
import {property, query, customElement, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {uploadPictureSvg} from '../../helpers/elements.ts';
import {createAddImageUrl} from '../../urlHandling/urlCreate.ts';
import {getPhotoUrl} from '../../urlHandling/urlSearchParams.ts';

export type UploadedEvent = {
  detail: {photo: string};
};

export type ImageValidity = 'valid' | 'temp';

@localized()
@customElement('element-poi-image')
export class ElementPoiImage extends LitElement {
  @property({type: String}) hint: string | undefined;
  @property({type: String}) photo?: string;
  @property({type: String}) validity?: ImageValidity;
  @state() uploadActive = false;
  @state() draggingOver = false;
  @state() errorMessage: string = undefined;
  @query('.upload-input') uploadInput: HTMLInputElement;
  @query('.icon-container') deleteIconContainer: HTMLDivElement;
  @query('.photo-image') img: HTMLImageElement;

  sizeIconContainer() {
    if (this.img) {
      this.img.onload = () => {
        if (this.deleteIconContainer) {
          this.deleteIconContainer.style.width = `${this.img.width}px`;
          this.deleteIconContainer.style.height = `${this.img.height}px`;
        }
      };
    }
  }

  async uploadImageFile(file: File) {
    if (!file) return;
    this.uploadActive = true;
    try {
      const photo = await this.saveImage(file);
      const validity = 'temp';
      this.errorMessage = undefined;
      this.dispatchEvent(
        new CustomEvent('uploaded', {detail: {photo, validity}}),
      );
    } catch (error) {
      if (error instanceof Error) this.errorMessage = error.message;
    }

    this.uploadActive = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingOver = false;
    this.uploadImageFile(event.dataTransfer.files[0]);
  }

  deleteImage() {
    this.photo = undefined;
    if (this.uploadInput) {
      this.uploadInput.files = undefined;
      this.uploadInput.value = '';
    }
    this.validity = undefined;
    this.dispatchEvent(new CustomEvent('deleted'));
  }

  async saveImage(file: File): Promise<string | undefined> {
    if (!file) return undefined;
    if (
      !['image/jpeg', 'image/png', 'image/heif'].includes(file.type) ||
      file.size > 10485760
    ) {
      throw new Error(msg('The file does not have the right format or size.'));
    } else {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response: Response = await fetch(createAddImageUrl(), {
          credentials: 'include',
          method: 'post',
          body: formData,
        });
        if (!response.ok) {
          throw new Error(msg('The file could not be accepted.'));
        }
        return (await response.json()).filename;
      } catch {
        throw new Error(msg('The file could not be accepted.'));
      }
    }
  }

  override updated(_changedProperties: PropertyValues) {
    this.sizeIconContainer();
  }

  renderDragArea() {
    return html` <button
      class="${classMap({
        'border-danger': this.errorMessage,
        'border-primary-gray': !this.errorMessage,
        'hover:border-chm-focus-red': !this.errorMessage,
        'hover:fill-chm-focus-red': !this.errorMessage,
        'disabled:border-chm-focus-red': !this.errorMessage,
        'md:border-chm-focus-red': this.draggingOver && !this.errorMessage,
        'md:fill-chm-focus-red': this.draggingOver && !this.errorMessage,
      })} h-full w-full border-[1px] border-dashed fill-chm-red"
      .disabled="${this.uploadActive}"
      @click="${() => this.uploadInput.click()}"
      @drop="${this.onDrop}"
      @dragenter="${() => {
        this.draggingOver = true;
      }}"
      @dragover="${(event: DragEvent) => {
        event.preventDefault();
      }}"
      @dragleave="${() => {
        this.draggingOver = false;
      }}"
    >
      <div
        class="pointer-events-none flex h-full flex-col p-[20px] text-chm-gray"
      >
        <div class="flex text-left">${this.hint}</div>
        <div
          .hidden="${!this.uploadActive}"
          class="bg-loader-animated mt-[18px] md:mt-[24px] h-[24px] w-[24px] self-center bg-no-repeat"
        ></div>
        <div
          .hidden="${this.uploadActive}"
          class="mt-[18px] md:mt-[24px] h-[24px] w-[24px] self-center bg-no-repeat"
        >
          ${uploadPictureSvg('h-[24px] w-[24px]')}
        </div>
        <div
          class="pl-[16px] text-chm-red pt-[10px]"
          .hidden="${!this.errorMessage}"
        >
          ${this.errorMessage}
        </div>
      </div>
    </button>`;
  }

  get renderPreview() {
    return html`
      <div class="relative h-full w-full">
        <img
          class="photo-image max-h-full object-cover bg-left bg-no-repeat bg-contain border-[1px] border-dashed border-primary-gray hover:border-chm-focus-red"
          src=${getPhotoUrl(this.photo, this.validity)}
        />
        <div class="icon-container absolute top-0 left-0">
          <div
            class="bg-delete relative top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[40px] w-[40px] cursor-pointer"
            @click="${this.deleteImage}"
          ></div>
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      <div class="body w-full aspect-[4/3]">
        ${this.photo ? this.renderPreview : this.renderDragArea()}
      </div>
      <input
        hidden
        type="file"
        accept="*/*"
        class="upload-input"
        @change=${(e: Event) => {
          const target = <HTMLInputElement>e.target;
          if (!target || !target.files?.length) return;
          this.uploadImageFile(target.files[0]);
        }}
      />
    `;
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-poi-image': ElementPoiImage;
  }
}
